import React, { useState } from 'react';
import { Plus, Trash2, Star } from 'lucide-react';
import type { WizardState } from '../types';
import type { StepError } from '../validateStep';
import ConfirmActionModal from '../../../../components/ui/ConfirmActionModal';
import { formatPhone } from '../../../../utils/phoneFormatting';
import ValidatedEmailInput from '../../../../components/ValidatedEmailInput';

interface Props {
  state: WizardState;
  onChange: (patch: Partial<WizardState>) => void;
  errors?: StepError[];
}

function emptyContact(isFirstContact: boolean = false) {
  return {
    _id: crypto.randomUUID(),
    name: '',
    title: '',
    isPrimary: isFirstContact,
    emails: [{ type: 'work', address: '', isPrimary: true }],
    phones: [{ type: 'work', number: '', isPrimary: true }],
    notes: '',
  };
}

export default function ContactsStep({ state, onChange, errors = [] }: Props) {
  const contacts = state.contacts;
  const hasError = (field: string) => errors.some((e) => e.field === field);
  const errCls = (field: string) => hasError(field) ? 'border-red-400 ring-1 ring-red-200' : 'border-gray-300';

  const updateContact = (idx: number, patch: Record<string, any>) => {
    const updated = [...contacts];
    updated[idx] = { ...updated[idx], ...patch };
    onChange({ contacts: updated });
  };

  const addContact = () => {
    const isFirstContact = contacts.length === 0;
    onChange({ contacts: [...contacts, emptyContact(isFirstContact)] });
  };

  const [removeIdx, setRemoveIdx] = useState<number | null>(null);

  const removeContact = (idx: number) => {
    const updated = contacts.filter((_, i) => i !== idx);
    onChange({ contacts: updated });
  };

  const setPrimary = (idx: number) => {
    const updated = contacts.map((c, i) => ({ ...c, isPrimary: i === idx }));
    onChange({ contacts: updated });
  };

  const updateEmail = (cIdx: number, eIdx: number, patch: Record<string, any>) => {
    const updated = [...contacts];
    const emails = [...updated[cIdx].emails];
    emails[eIdx] = { ...emails[eIdx], ...patch };
    updated[cIdx] = { ...updated[cIdx], emails };
    onChange({ contacts: updated });
  };

  const addEmail = (cIdx: number) => {
    const updated = [...contacts];
    updated[cIdx] = { ...updated[cIdx], emails: [...updated[cIdx].emails, { type: 'work', address: '', isPrimary: false }] };
    onChange({ contacts: updated });
  };

  const removeEmail = (cIdx: number, eIdx: number) => {
    const updated = [...contacts];
    const emails = updated[cIdx].emails.filter((_, i) => i !== eIdx);
    // Ensure at least one email remains
    if (emails.length === 0) return;
    updated[cIdx] = { ...updated[cIdx], emails };
    onChange({ contacts: updated });
  };

  const updatePhone = (cIdx: number, pIdx: number, patch: Record<string, any>) => {
    const updated = [...contacts];
    const phones = [...updated[cIdx].phones];
    phones[pIdx] = { ...phones[pIdx], ...patch };
    updated[cIdx] = { ...updated[cIdx], phones };
    onChange({ contacts: updated });
  };

  const addPhone = (cIdx: number) => {
    const updated = [...contacts];
    updated[cIdx] = { ...updated[cIdx], phones: [...updated[cIdx].phones, { type: 'work', number: '', isPrimary: false }] };
    onChange({ contacts: updated });
  };

  const removePhone = (cIdx: number, pIdx: number) => {
    const updated = [...contacts];
    const phones = updated[cIdx].phones.filter((_, i) => i !== pIdx);
    // Ensure at least one phone remains
    if (phones.length === 0) return;
    updated[cIdx] = { ...updated[cIdx], phones };
    onChange({ contacts: updated });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Add at least one primary contact for this department.</p>
        <button type="button" onClick={addContact} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-orange-700 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
          <Plus className="h-4 w-4" /> Add Contact
        </button>
      </div>

      {contacts.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <p className="text-gray-400 text-sm">No contacts added yet</p>
        </div>
      )}

      {contacts.map((contact, cIdx) => (
        <div key={contact._id || cIdx} className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">Contact {cIdx + 1}</span>
              {contact.isPrimary && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                  <Star className="h-3 w-3" /> Primary
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!contact.isPrimary && (
                <button type="button" onClick={() => setPrimary(cIdx)} className="text-xs text-gray-500 hover:text-amber-600">Set as Primary</button>
              )}
              <button type="button" onClick={() => setRemoveIdx(cIdx)} className="text-gray-400 hover:text-red-500">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
              <input type="text" value={contact.name} onChange={(e) => updateContact(cIdx, { name: e.target.value })} className={`w-full px-3 py-2 border ${errCls(`contacts[${cIdx}].name`)} rounded-lg text-sm`} placeholder="John Smith" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
              <input type="text" value={contact.title || ''} onChange={(e) => updateContact(cIdx, { title: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Fire Marshal" />
            </div>
          </div>

          {/* Emails */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-600">Emails</label>
              <button type="button" onClick={() => addEmail(cIdx)} className="text-xs text-orange-600 hover:text-orange-700">+ Add Email</button>
            </div>
            {contact.emails.map((email, eIdx) => (
              <div key={eIdx} className="flex items-center gap-2 mb-2">
                <select value={email.type} onChange={(e) => updateEmail(cIdx, eIdx, { type: e.target.value })} className="w-24 px-2 py-1.5 border border-gray-300 rounded text-xs">
                  <option value="work">Work</option>
                  <option value="personal">Personal</option>
                </select>
                <ValidatedEmailInput value={email.address} onChange={(v) => updateEmail(cIdx, eIdx, { address: v })} className={`flex-1 px-3 py-1.5 border ${errCls(`contacts[${cIdx}].emails`)} rounded text-sm pr-9`} placeholder="email@example.com" />
                {contact.emails.length > 1 && (
                  <button type="button" onClick={() => removeEmail(cIdx, eIdx)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors" aria-label="Remove email">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Phones */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-600">Phones</label>
              <button type="button" onClick={() => addPhone(cIdx)} className="text-xs text-orange-600 hover:text-orange-700">+ Add Phone</button>
            </div>
            {contact.phones.map((phone, pIdx) => (
              <div key={pIdx} className="flex items-center gap-2 mb-2">
                <select value={phone.type} onChange={(e) => updatePhone(cIdx, pIdx, { type: e.target.value })} className="w-24 px-2 py-1.5 border border-gray-300 rounded text-xs">
                  <option value="work">Work</option>
                  <option value="mobile">Mobile</option>
                  <option value="fax">Fax</option>
                </select>
                <input type="tel" value={phone.number} onChange={(e) => updatePhone(cIdx, pIdx, { number: formatPhone(e.target.value) })} className="px-3 py-1.5 border border-gray-300 rounded text-sm pr-9" placeholder="(555) 123-4567" />
                {contact.phones.length > 1 && (
                  <button type="button" onClick={() => removePhone(cIdx, pIdx)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors" aria-label="Remove phone">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea rows={2} value={contact.notes || ''} onChange={(e) => updateContact(cIdx, { notes: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
        </div>
      ))}
      {removeIdx !== null && (
        <ConfirmActionModal
          title="Remove Contact?"
          description={<>Remove <strong>{contacts[removeIdx]?.name || `Contact ${removeIdx + 1}`}</strong> from this draft? This cannot be undone.</>}
          confirmButtonLabel="Yes, Remove"
          variant="warning"
          onConfirm={() => { removeContact(removeIdx); setRemoveIdx(null); }}
          onClose={() => setRemoveIdx(null)}
        />
      )}
    </div>
  );
}
